<script lang="ts" setup>
import { useRouter, useRoute } from 'vue-router'
import { DynamicForm } from '../../dynamic-form'
import { getForm } from '../../../core/services/forms'
import { onBeforeMount, ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { DLoading } from 'front-library'
import { useI18n } from 'vue-i18n'
import {
  getCompany,
  createCompany,
  updateCompany
} from '../../../core/services/companies'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* router, notifications */
const notifications = useNotificationsStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const customForm = ref<any>({})
let model: any = {}
const dataFile: any = ref<any>({})

const hasDuplicateCodifications = (model: any): boolean => {
  if (Array.isArray(model.additionalCodifications)) {
    const seen = new Set<string>()
    const duplicates = model.additionalCodifications.filter((item: any) => {
      if (seen.has(item.name)) return true
      seen.add(item.name)
      return false
    })
    if (duplicates.length > 0) {
      loading.value = false
      notifications.addNotification({
        title: t('status.ERROR'),
        text: t('companies.notification.errors.codificationsDuplicate'),
        type: 'error',
        time: 10000
      })
      return true
    }
  }
  return false
}

const prepareCodificationsModel = (model: any) => {
  let isError = false
  model.additionalCodifications = model.additionalCodifications.map(
    (codification: any) => {
      if (codification.maxLength < codification.minLength) {
        notifications.addNotification({
          title: t('status.ERROR'),
          text: t('companies.notification.errors.conflictLengthCodifications'),
          type: 'error',
          time: 10000
        })
        isError = true
      }

      return {
        id: codification.id,
        name: codification.name,
        type: codification.type,
        minLength: Number(codification.minLength),
        maxLength: Number(codification.maxLength),
        placeholder: codification.placeholder,
        isMandatory: Boolean(codification.isMandatory),
        isActive: Boolean(codification.isActive)
      }
    }
  )
  return isError
}

const onSubmit = async (model: any) => {
  loading.value = true
  let saved = false
  try {
    if (hasDuplicateCodifications(model)) {
      loading.value = false
      return
    }

    if (prepareCodificationsModel(model)) {
      loading.value = false
      return
    }

    if (!route.params.id) {
      saved = await createCompany(model)
    } else {
      saved = await updateCompany(model, `${route.params.id as string}`)
    }
    if (saved) {
      loading.value = false
      notifications.addNotification({
        title: !route.params.id
          ? t('notification.created')
          : t('notification.edited'),
        text: t('companies.notification.action', [
          !route.params.id
            ? t('notification.wasCreated')
            : t('notification.wasEdited')
        ]),
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'ListCompanies' })
    }
  } catch (error: any) {
    notificationError(error, 'companies', t, notifications, loading)
  }
}

const getEntity = async () => {
  if (!route.params.id) return
  model = await getCompany(route.params.id as string)
  if (model === undefined) {
    notifications.addNotification({
      title: t('notification.notFound'),
      text: t('companies.notification.errors.notFound'),
      type: 'error',
      time: 10000
    })
    back()
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListCompanies' })
}

onBeforeMount(async () => {
  loading.value = true
  try {
    await getEntity()
    customForm.value = await getForm({
      entity: 'companies',
      country: model.isoCode,
      company: model.id ?? '',
      integrations: []
    })
    delete model.isoCode
    if (route.params.id !== undefined) messageLoading.value = t('loading.data')
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('companies.notification.errors.notFound'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'companies', t, notifications, loading)
    }
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="
            route.params.id == undefined
              ? t('companies.create')
              : t('companies.edit')
          " />
      </div>
    </div>
    <DynamicForm
      v-if="customForm?.tabs"
      :form-schema="customForm"
      :initial-model="model"
      :title-btn-save="
        route.params.id === undefined
          ? t('form.button.save')
          : t('form.button.update')
      "
      @cancel="back"
      @submit="onSubmit"
      @data-file="dataFile = $event" />
  </div>
</template>
